import { useEffect, useState } from 'react';
import {
  getProjectBudgetContext,
  type BudgetContext,
} from '@/services/api/budgetContextApi';
import { getBudgetContext as getMockBudgetContext } from '@/data/mockBudgetContext';

interface UseBudgetContextOptions {
  projectId?: string | number | null;
  employeeId?: number | null;
  screenMode: 'demo' | 'live';
  /** Demo-mode lookup keys. */
  projectCode?: string;
  costCentreName?: string;
  /** Skip fetching entirely (e.g. the column is hidden). */
  enabled?: boolean;
}

interface KeyedContext {
  key: string;
  context: BudgetContext | null;
}

export function useBudgetContext({
  projectId,
  employeeId,
  screenMode,
  projectCode,
  costCentreName,
  enabled = true,
}: UseBudgetContextOptions): BudgetContext | null {
  const isDemo = screenMode === 'demo';
  // Live budget context is per project + employee; the demo lookup keys only apply in demo mode.
  const demoProjectCode = isDemo ? projectCode : undefined;
  const demoCostCentreName = isDemo ? costCentreName : undefined;
  // A context is only returned for the inputs it was loaded for, so a row whose project has
  // just changed never sees the previous project's figures while the new ones load.
  const key = JSON.stringify([
    enabled, isDemo, projectId ?? null, employeeId ?? null,
    demoProjectCode ?? null, demoCostCentreName ?? null,
  ]);
  const [loaded, setLoaded] = useState<KeyedContext>(() => ({
    key,
    context: isDemo && enabled ? getMockBudgetContext(demoProjectCode, demoCostCentreName) : null,
  }));

  useEffect(() => {
    if (!enabled) {
      setLoaded({ key, context: null });
      return;
    }

    if (isDemo) {
      setLoaded({ key, context: getMockBudgetContext(demoProjectCode, demoCostCentreName) });
      return;
    }

    if (projectId == null || projectId === '' || employeeId == null || employeeId <= 0) {
      setLoaded({ key, context: null });
      return;
    }

    // apiClient de-duplicates and short-term caches identical GETs, so rows on
    // the same project share one request.
    let cancelled = false;
    void getProjectBudgetContext(projectId, employeeId)
      .catch(() => null)
      .then(result => {
        if (!cancelled) setLoaded({ key, context: result });
      });

    return () => {
      cancelled = true;
    };
  }, [key, enabled, isDemo, projectId, employeeId, demoProjectCode, demoCostCentreName]);

  return loaded.key === key ? loaded.context : null;
}
